import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { tap } from 'rxjs';
import { ApplicationFeature } from 'src/app/feature/interfaces/application-feature';
import { FeaturesActions } from './features.actions';
import { ApiFeatureService } from 'src/app/predictor/services/api-feature.service';
import { ApplicationFeatureAdditionalDetails } from '../../feature/interfaces/application-feature-additional-details';

export enum FeatureFlagKey {
  SimulationDetails = 'Simulation Details',
  FavoriteSimulationLimit = 'Favorite Simulation Limit',
  SimulationHistoryLimit = 'Simulation History Limit',
  BatchInitiationPointThreshold = 'Batch Initiation Point Threshold',
  BatchLoopsThreshold = 'Batch Loops Threshold',
  EnterpriseLicense = 'Enterprise License',
}

export interface FeaturesStateModel {
  applicationFeatures: ApplicationFeature[];
  features: Record<FeatureFlagKey, ParsedFeatureValue>;
}

const stateDefaults: FeaturesStateModel = {
  applicationFeatures: [],
  features: {
    [FeatureFlagKey.SimulationDetails]: false,
    [FeatureFlagKey.FavoriteSimulationLimit]: 3,
    [FeatureFlagKey.SimulationHistoryLimit]: 10,
    [FeatureFlagKey.BatchInitiationPointThreshold]: 10000,
    [FeatureFlagKey.BatchLoopsThreshold]: 100,
    [FeatureFlagKey.EnterpriseLicense]: false,
  }
};

export type ParsedFeatureValue = boolean | number;

@State<FeaturesStateModel>({
  name: 'Features',
  defaults: stateDefaults,
})
@Injectable()
export class FeaturesState {
  constructor(private apiFeatureService: ApiFeatureService) {}

  static selectFeatureByKey(key: FeatureFlagKey) {
    return createSelector(
      [FeaturesState],
      (state: FeaturesStateModel): ParsedFeatureValue | undefined => {
        return state.features[key];
      }
    );
  }

  static selectFeatureNumberByKey(key: FeatureFlagKey) {
    return createSelector(
      [FeaturesState],
      (state: FeaturesStateModel): number => {
        const val: ParsedFeatureValue | undefined = state.features[key];
        return typeof val === 'number' ? val : 0;
      }
    );
  }

  @Selector()
  static featureFlags(state: FeaturesStateModel): Partial<Record<FeatureFlagKey, ParsedFeatureValue>> {
    return state.features;
  }

  @Selector()
  static simulationDetailsEnabled(FeaturesStateModel: FeaturesStateModel): boolean {
    const simulationDetailsFeature: ApplicationFeature | undefined = FeaturesStateModel.applicationFeatures.find((feature) => feature.name == "Simulation Details");
    return (simulationDetailsFeature !== undefined && simulationDetailsFeature.additionalProperties[0].value == "On");
  }

@Selector()
  static shareStudyAreaFeatureEnabled(FeaturesStateModel: FeaturesStateModel): boolean {
    const shareStudyAreaFeature: ApplicationFeature | undefined = FeaturesStateModel.applicationFeatures.find((feature) => feature.name == "Share Study Area");
    return (shareStudyAreaFeature !== undefined && shareStudyAreaFeature.additionalProperties[0].value == "On");
  }

   @Selector()
  static enterpriseLicenseEnabled(FeaturesStateModel: FeaturesStateModel): boolean {
    const elFeature: ApplicationFeature | undefined = FeaturesStateModel.applicationFeatures.find((feature) => feature.name == "Enterprise License");
    return (elFeature !== undefined && elFeature.additionalProperties[0].value == "On");
  }

  @Action(FeaturesActions.SetApplicationFeatures)
  setApplicationFeatures(ctx: StateContext<FeaturesStateModel>) {
    return this.apiFeatureService.getFeatures().pipe(
      tap((features: any) => {
        const applicationFeatures: ApplicationFeature[] = features.$values.map((feature: any) => {
          return {
            name: feature.name,
            description: feature.description,
            additionalProperties: feature.additionalProperties.$values.map((property: any) => {
              return {
                name: property.name,
                dataType: property.dataType,
                value: property.value
              }
            })
          }
        })

        const completeParsed: Record<FeatureFlagKey, ParsedFeatureValue> = {
          ...stateDefaults.features,
          ...this.parseFeatureFlags(applicationFeatures),
        };
        ctx.patchState({ applicationFeatures: applicationFeatures, features: completeParsed });
      })
    )
  }

  parseFeatureFlags(
    features: ApplicationFeature[]
  ): Partial<Record<FeatureFlagKey, ParsedFeatureValue>> {
    const parsedFlags: Partial<Record<FeatureFlagKey, ParsedFeatureValue>> = {};

    const enumValues: FeatureFlagKey[] = Object.values(FeatureFlagKey);

    for (const feature of features) {
      const featureName: string = feature.name;
      const key: FeatureFlagKey = featureName as FeatureFlagKey;

      const isKnownKey: boolean = enumValues.includes(key);
      if (!isKnownKey) {
        console.warn(`Unknown feature key: ${featureName}`);
        continue;
      }

      const valueEntry: ApplicationFeatureAdditionalDetails | undefined =
        feature.additionalProperties.find(
          (property: ApplicationFeatureAdditionalDetails): boolean =>
            property.name === 'featureDefault'
        );

      if (!valueEntry) {
        continue;
      }

      let parsedValue: ParsedFeatureValue;

      if (valueEntry.dataType === 'Boolean') {
        const rawValue: string = valueEntry.value;
        parsedValue = rawValue.toLowerCase() === 'on';
      } else if (valueEntry.dataType === 'Numerical') {
        const rawValue: string = valueEntry.value;
        parsedValue = Number(rawValue || 0);
      } else {
        console.warn(`Unknown data type: ${valueEntry.dataType}`);
        continue;
      }

      parsedFlags[key] = parsedValue;
    }

    return parsedFlags;
  }

}
